/**
 * System for determining the "letter spacing correction" to account for incorrect fonts resulting from
 * misconfigured/buggy browsers and operating systems. This is expected to be 0 always for almost all users,
 * but exists to correct for buggy visuals that some users have seen.
 *
 * All UI elements that rely on font metrics should listen to the FontAvailability singleton and call
 * getLetterSpacingCorrection whenever fonts are available.
 *
 * In various places in the molecular biology suite, we serve the monospace font Inconsolata and make
 * assumptions about the font measurements, e.g. that 14-point font will be exactly 7 pixels wide. This is
 * needed so that we can show long DNA/AA strings to the user and draw other UI elements (cursor, annotations,
 * restriction sites). In almost all cases, this works fine, but some users have seen incorrect fonts for
 * reasons that aren't always understood. This is effectively a data integrity bug: a user might select ACTG
 * and hit copy and get CTGC copied because the font was drawn incorrectly.
 *
 * To address this issue, we programmatically measure the text width once fonts are loaded and determine a
 * letter-spacing value that will adjust the text to line up as expected.
 *
 * One way to test this mechanism is to use Chrome Dev Tools to block the inconsolata woff files. This will
 * cause the browser to fall back to the Courier New font, and when MOLBIO_MONOSPACE_FONT_CORRECTION_AUTOFIX
 * is enabled, the font spacing in the DNA and protein tools should be automatically adjusted. Long alignments
 * are especially susceptible to small inaccuracies in the measurement.
 *
 * The feature itself is controlled by two flags:
 * - MOLBIO_MONOSPACE_FONT_CORRECTION_WARN, which can be used to measure how often this issue comes up for a
 *     tenant.
 * - MOLBIO_MONOSPACE_FONT_CORRECTION_AUTOFIX, which will automatically adjust font spacing to correct.
 *
 * The ideal state is to have autofix on and warn off, but the flags exist in case the measurement/correction
 * logic is misbehaving and to detect how widespread the issue is.
 *
 * An alternative approach that was considered was to position each base/AA as its own DOM node so that we're
 * never relying on font metrics, but that strategy has known performance issues, especially for alignment.
 */

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import Type from 'molbio/maps/Type';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {SingletonModel} from 'util/backbone-extensions';
import * as logging from 'util/Logging';

let cachedLetterSpacingCorrection: {[key: number]: number} = {};

/**
 * After FontAvailability indicates that fonts have loaded, this function can be called to get a
 * letter-spacing value that accounts for a potentially incorrect font.
 */
export function getLetterSpacingCorrection(fontSize: number): number {
  if (
    !assertAndContinue(
      FontAvailability.get().get('areFontsReady'),
      'getLetterSpacingCorrection should only be called once fonts are ready.'
    )
  ) {
    return 0;
  }
  if (!cachedLetterSpacingCorrection.hasOwnProperty(fontSize)) {
    cachedLetterSpacingCorrection[fontSize] = calculateLetterSpacingCorrection(fontSize);
  }
  return cachedLetterSpacingCorrection[fontSize];
}

/**
 * Programmatically determine the difference between expected and actual font size for the monospace font. The
 * returned value can be passed as the letter-spacing property so that the font measurements match those in
 * Type.ts.
 *
 * To actually measure the text, we use the canvas method measureText, which has sub-pixel accuracy, unlike
 * other approaches like the jQuery width() method. Each font size is measured lazily because a typical user
 * session will only need a small number of font sizes.
 */
function calculateLetterSpacingCorrection(fontSize: number): number {
  const canvas = document.createElement('canvas');
  const canvasContext = canvas.getContext('2d')!;

  canvasContext.font = `${fontSize}px ${FONT_FAMILY_MONOSPACED}`;
  // Hopefully an incorrect font is still monospace, but in case it's not, measure the average width of the
  // common characters as a best effort attempt at making the text line up.
  const measuredWidth = canvasContext.measureText('atcgACTG').width / 8;
  if (measuredWidth === 0 || Number.isNaN(measuredWidth)) {
    // If the browser can't give a measurement, don't try to correct letter spacing.
    return 0;
  }
  const expectedWidth = new Type(fontSize).width(1);
  if (measuredWidth !== expectedWidth && BenchlingConfig.MOLBIO_MONOSPACE_FONT_CORRECTION_WARN) {
    // In several situations, we are known to have mismatches with no or minimal effect and should just skip
    // the warning but still perform the correction:
    // * Font size 3 happens when fully zoomed out for DNA.
    // * Font size 15 happens when fully zoomed out for AA alignments.
    // * Measurements are sometimes only very slighly off, presumably due to round-off.
    //
    // See https://benchling.slack.com/archives/CC0FJLHTR/p1624520226001500?thread_ts=1623286110.276900&cid=CC0FJLHTR
    const shouldSkipWarn =
      fontSize === 3 || fontSize === 15 || Math.abs(measuredWidth - expectedWidth) < 0.0001;
    if (!shouldSkipWarn) {
      logging.warn(
        `Mismatched letter spacing for size ${fontSize}: expected ${expectedWidth}, got ${measuredWidth}`
      );
    }
  }
  if (BenchlingConfig.MOLBIO_MONOSPACE_FONT_CORRECTION_AUTOFIX) {
    return expectedWidth - measuredWidth;
  } else {
    return 0;
  }
}

interface FontAvailabilityAttributes {
  areFontsReady: boolean;
}

/**
 * Backbone singleton to indicate if font measurements are available. The getLetterSpacingCorrection may not
 * be called until this returns true.
 *
 * Usage: FontAvailability.get().get('areFontsReady')
 */
export class FontAvailability extends SingletonModel {
  defaults(): FontAvailabilityAttributes {
    return {
      areFontsReady: false,
    };
  }
}

/**
 * Listen to the document state to declare when fonts are available for measurement. This must be called at
 * application startup when using getLetterSpacingCorrection.
 */
export function initMonospaceFontCorrection(): void {
  // If both feature flags are disabled, don't attempt to measure fonts and never re-render components.
  if (
    !BenchlingConfig.MOLBIO_MONOSPACE_FONT_CORRECTION_WARN &&
    !BenchlingConfig.MOLBIO_MONOSPACE_FONT_CORRECTION_AUTOFIX
  ) {
    return;
  }

  // document.fonts.ready is the most precise way to measure font readiness, but is only available in newer
  // browsers. Use the load event as a fallback.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (document as any).fonts?.ready?.then(() => {
    onFontsReady();
  });
  if (document.readyState === 'complete') {
    onFontsReady();
  }
  window.addEventListener('load', () => {
    onFontsReady();
  });
}

function onFontsReady(): void {
  FontAvailability.get().set({areFontsReady: true});
}

export function resetMonospaceFontCorrectionForTests(): void {
  FontAvailability.resetForTests();
  cachedLetterSpacingCorrection = {};
}
